import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import {
  GroupVarKey,
  LeasePropKey,
  LocationVarKey,
  OrgVarKey,
  UnitPropKey,
  UserPropKey,
  UserVarKey,
  VarDef,
  VarDefKeys,
  VarDefinition,
  VarDefinitionsType,
  VarKey,
  VarType,
} from "@swivl-mono/types";
import { Button, ButtonColor, Checkbox, Input } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { AddVariableModal, NewVariableState } from "./Components/AddVariableModal/AddVariableModal";
import { AdminVariablesEditorVariable } from "./Components/Admin.VariableEditorVariable";

const camelToFlat = (camel: string) => {
  const camelCase = camel.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");

  let flat = "";

  camelCase.forEach((word) => {
    flat = flat + word.charAt(0).toUpperCase() + word.slice(1) + " ";
  });
  return flat;
};
export function AdminVariablesEditor() {
  const [filter, setFilter] = useState<{
    types: VarType[];
    searchQuery?: string;
  }>({
    types: [VarType.Location, VarType.User, VarType.UserVar, VarType.Unit, VarType.Org, VarType.Lease, VarType.Group],
    searchQuery: "",
  });
  const [data, setData] = useState<VarDefinitionsType | undefined>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  const filterTypeChanged = (type: VarType, checked: boolean) => {
    if (checked) {
      setFilter({ ...filter, types: [...filter.types, type] });
    } else {
      setFilter({ ...filter, types: filter.types.filter((t) => t !== type) });
    }
  };

  useEffect(() => {
    let d: VarDefinitionsType = { ...(VarDef as VarDefinitionsType) };
    let isDirty = false;
    Object.values(LocationVarKey).forEach((key) => {
      const varKey = (VarType.Location + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.Location,
          displayName: camelToFlat(key.replace("Location", "Location - ")),
          description: "",
          defaultValue: "",
          protected: false,
        };
        d[varKey] = item;
      }
    });


    Object.values(UserPropKey).forEach((key) => {
      const varKey = (VarType.UserVar + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.User,
          displayName: camelToFlat(key.replace("User", "User - ")),
          description: "",
          defaultValue: "",
          protected: false,
        };
        d[varKey] = item;
      }
    });

    Object.values(UserVarKey).forEach((key) => {
      const varKey = ("User:" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.UserVar,
          displayName: camelToFlat(key.replace("User", "User Var - ")),
          description: "",
          defaultValue: "",
          protected: false,
        };
        d[varKey] = item;
      }
    });

    Object.values(UnitPropKey).forEach((key) => {
      const varKey = (VarType.Unit + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.Unit,
          displayName: camelToFlat(key.replace("Unit", "Unit - ")),
          description: "",
          defaultValue: "",
          protected: true,
        };
        d[varKey] = item;
      }
    });

    Object.values(OrgVarKey).forEach((key) => {
      const varKey = (VarType.Org + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.Org,
          displayName: camelToFlat(key.replace("Org", "Org - ")),
          description: "",
          defaultValue: "",
          protected: true,
        };
        d[varKey] = item;
      }
    });
    Object.values(GroupVarKey).forEach((key) => {
      const varKey = (VarType.Group + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key,
          varKey: varKey,
          type: VarType.Group,
          displayName: camelToFlat(key.replace("Group", "Group - ")),
          description: "",
          defaultValue: "",
          protected: true,
        };
        d[varKey] = item;
      }
    });
    Object.values(LeasePropKey).forEach((key) => {
      const varKey = (VarType.Lease + ":" + key) as VarKey;

      if (!d[varKey]) {
        isDirty = true;

        let item: VarDefinition = {
          key: key as any,
          varKey: varKey,
          type: VarType.Lease,
          displayName: camelToFlat(key.replace("Lease", "Lease - ")),
          description: "",
          defaultValue: "",
          protected: true,
        };
        d[varKey] = item;
      }
    });

    setData(d as VarDefinitionsType);
    setIsDirty(isDirty);
    setIsLoading(false);
  }, []);

  const updateVar = (v: VarDefinition) => {
    let d: VarDefinitionsType = { ...data };
    d[v.varKey] = v;
    setData(d);
    setIsDirty(true);
  };
  const deleteVar = (v: VarDefinition) => {
    if (window.confirm("Are you sure you want to delete this variable?")) {
      let d: VarDefinitionsType = { ...data };
      delete d[v.varKey];
      setData(d);
      setIsDirty(true);
    }
  };

  const saveData = () => {
    if (!data) {
      warn("No data to save");
      return;
    }
    setIsLoading(true);

    API.Vars.saveEditorData(data).then((res) => {
      setIsLoading(false);
    });
    setIsDirty(false);
  };

  let items: any = [];
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const item = data[key as VarKey];

      if (item) {
        if (!filter.types.includes(item.type)) {
          continue;
        }
        if (filter.searchQuery && !item.displayName.toLowerCase().includes(filter.searchQuery.toLowerCase())) {
          continue;
        }
        items.push(<AdminVariablesEditorVariable key={item.varKey} variable={item} updateVar={updateVar} deleteVar={deleteVar} />);
      }
    }
  }

  const createNewVariable = (state: NewVariableState) => {
    let d: VarDefinitionsType = { ...data };
    let varKey = (state.type + ":" + state.key) as VarKey;

    const varKeyKeysLowercase = Object.keys(d).map((k) => k.toLowerCase());

    if (d[varKey] || varKeyKeysLowercase.includes(varKey.toLowerCase())) {
      alert("Variable with that key already exists");
      return;
    }

    let v: VarDefinition = {
      type: state.type as VarType,
      key: state.key as VarDefKeys,
      varKey: varKey,
      displayName: state.displayName,
      description: state.description,
      defaultValue: state.defaultValue,
    };

    d[v.varKey] = v;

    setData(d);
    setIsDirty(true);
    setIsAdding(false);
  };

  return (
    <div className="p-5">
      <div className="flex flex-row mb-4">
        <Input
          className="mr-2 w-1/4"
          onChange={(e) => setFilter({ ...filter, searchQuery: e.target.value })}
          placeholder="Search"
          value={filter.searchQuery}
        />
        <Checkbox
          checked={filter.types.includes(VarType.Location)}
          classNames={{ wrapper: "mr-2" }}
          label="Locations"
          name={VarType.Location}
          onCheckedChange={(checked) => filterTypeChanged(VarType.Location, Boolean(checked))}
        />

        <Checkbox
          checked={filter.types.includes(VarType.User)}
          classNames={{ wrapper: "mr-2" }}
          label="Users"
          name={VarType.User}
          onCheckedChange={(checked) => filterTypeChanged(VarType.User, Boolean(checked))}
        />

        <Checkbox
          checked={filter.types.includes(VarType.Unit)}
          classNames={{ wrapper: "mr-2" }}
          label="Units"
          name={VarType.Unit}
          onCheckedChange={(checked) => filterTypeChanged(VarType.Unit, Boolean(checked))}
        />

        <Checkbox
          checked={filter.types.includes(VarType.Org)}
          classNames={{ wrapper: "mr-2" }}
          label="Org"
          name={VarType.Org}
          onCheckedChange={(checked) => filterTypeChanged(VarType.Org, Boolean(checked))}
        />

        <Checkbox
          checked={filter.types.includes(VarType.Group)}
          classNames={{ wrapper: "mr-2" }}
          label="Group"
          name={VarType.Group}
          onCheckedChange={(checked) => filterTypeChanged(VarType.Group, Boolean(checked))}
        />

        <Checkbox
          checked={filter.types.includes(VarType.Lease)}
          classNames={{ wrapper: "mr-2" }}
          label="Lease"
          name={VarType.Lease}
          onCheckedChange={(checked) => filterTypeChanged(VarType.Lease, Boolean(checked))}
        />

        <div className="flex-1">
          {isDirty && (
            <Button loading={isLoading} onClick={saveData} className="ml-3" color={ButtonColor.ORANGE}>
              Save
            </Button>
          )}{" "}
        </div>
        <div className="flex-1">
          <Button
            onClick={() => {
              setIsAdding(true);
            }}
          >
            Add New Variable
          </Button>
        </div>
      </div>
      {isAdding && <AddVariableModal createNewVariable={createNewVariable} />}

      {items}
    </div>
  );
}
